/**
 * AgentChat Component
 *
 * Main interface for AI-driven dashboard generation.
 * Users can describe their dashboard needs and Claude generates the configuration.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAgent } from '../../contexts/AgentContext';
import ChatMessage from './ChatMessage';
import GeneratedDashboardPreview from './GeneratedDashboardPreview';
import './AgentChat.css';

const AgentChat = () => {
  const {
    messages,
    loading,
    error,
    generate,
    clearChat,
    generatedDashboard
  } = useAgent();

  const [inputValue, setInputValue] = useState('');
  const [period, setPeriod] = useState('Monthly');
  const [template, setTemplate] = useState('four_columns_three_rows');
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || loading) return;

    const objective = inputValue.trim();
    setInputValue('');

    try {
      await generate(objective, period, template);
    } catch (err) {
      // Error handling is done in context
      console.error('Generation error:', err);
    }
  };

  const handleClear = () => {
    if (window.confirm('Clear chat history and generated dashboard?')) {
      clearChat();
    }
  };

  return (
    <div className="agent-chat-container">
      <div className="agent-chat-header">
        <h2>AI Dashboard Generator</h2>
        <p className="agent-chat-subtitle">
          Describe your dashboard needs and Claude will generate the configuration
        </p>
        {messages.length > 0 && (
          <button
            className="clear-chat-button"
            onClick={handleClear}
            disabled={loading}
          >
            Clear Chat
          </button>
        )}
      </div>

      <div className="agent-chat-layout">
        <div className="chat-section">
          <div className="messages-container">
            {messages.length === 0 && (
              <div className="welcome-message">
                <h3>Welcome to the AI Dashboard Generator!</h3>
                <p>I'm your intelligent dashboard assistant. I can help you create dashboards through conversation.</p>

                <h4>Try asking for things like:</h4>
                <ul>
                  <li>"The CEO wants to look at sales"</li>
                  <li>"Show me marketing performance metrics"</li>
                  <li>"Create a dashboard for gross profit and orders"</li>
                  <li>"I need to analyze customer metrics"</li>
                </ul>

                <h4>You can also refine dashboards iteratively:</h4>
                <ul>
                  <li>"Add revenue to this dashboard"</li>
                  <li>"Change the period to quarterly"</li>
                  <li>"Make it show year-over-year growth"</li>
                  <li>"Remove the customer chart"</li>
                </ul>

                <p className="tip">
                  <strong>Tip:</strong> I'll ask clarifying questions if I need more details. Just chat naturally!
                </p>
              </div>
            )}

            {messages.map((message, index) => (
              <ChatMessage key={index} message={message} />
            ))}

            {loading && (
              <div className="loading-message">
                <div className="loading-spinner"></div>
                <span>Claude is generating your dashboard...</span>
              </div>
            )}

            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <form className="chat-input-form" onSubmit={handleSubmit}>
            <div className="input-options">
              <div className="option-group">
                <label htmlFor="period-select">Period:</label>
                <select
                  id="period-select"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  disabled={loading}
                >
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>

              <div className="option-group">
                <label htmlFor="template-select">Template:</label>
                <select
                  id="template-select"
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  disabled={loading}
                >
                  <option value="one_left_right_three_section">One Left + Three Right</option>
                  <option value="four_columns_three_rows">Four Columns x Three Rows</option>
                  <option value="four_top_bottom_two_section">Four Top + Two Bottom</option>
                </select>
              </div>
            </div>

            <div className="input-row">
              <textarea
                className="chat-input"
                placeholder="Describe your dashboard needs... (e.g., 'The CEO wants to look at sales')"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                disabled={loading}
                rows={3}
              />
              <button
                type="submit"
                className="send-button"
                disabled={!inputValue.trim() || loading}
              >
                {loading ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </form>
        </div>

        {generatedDashboard && (
          <div className="preview-section">
            <GeneratedDashboardPreview />
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentChat;
